import React, { memo, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { Form } from 'semantic-ui-react';
import { Space, Button } from '@cashfree-intl/coherent';

import { ALIAS_LOGIN } from 'utils/constants';
import {
  generateReCaptcha,
  resetReCaptcha,
  getDeviceId,
  captureBlur,
  useQuery,
} from 'utils/common';

import Analytics from 'utils/analytics';
import EVENTS from 'constants/analytics';

import FormikComponent from 'components/Formik';
import FormInput from 'components/FormInput';
import CustomIcon from 'components/CustomIcons';
import { AuthContext } from '../../../contexts/AuthContext';

import { aliasLoginSchema as validationSchema } from '../validation';
import { trackLoginEvent } from '../helper';

function AliasLogin(props) {
  const [showEyeIcon, toggleEyeIcon] = useState(false);
  const { userObj, latitude, longitude } = useContext(AuthContext);

  const {
    aliasLogin,
    navigateAsPerUserFlow,
    authenticateAlias,
    enableLoader,
    onboardingActions,
  } = props;

  const loginAlias = async (values, actions, captchaResponse) =>
    authenticateAlias({
      body: {
        ...values,
        captchaResponse: captchaResponse || ' ',
        deviceId: await getDeviceId(),
        latitude,
        longitude,
      },
      actions,
      cb: (data, token) => {
        userObj.login(data);
        navigateAsPerUserFlow(token);
        trackLoginEvent({
          loginType: 'alias_login',
          status: 'success',
          latitude,
          longitude,
          locationPermissionGranted: !!latitude,
          aliasAccountName: values.accountAlias,
          aliasUserName: values.username,
        });
      },
    });

  const submitAliasLogin = twofaActions => (values, actions) => {
    const envObj = process.env;
    const isGamma = envObj.NODE_ENV === 'gamma';
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const byPassReCaptcha = useQuery().get('recpt') === '0';

    Analytics.trackEvent(EVENTS[ALIAS_LOGIN].LOGIN_BTN, null, 'OB')();
    enableLoader();

    const onSubmitReCaptcha = async (captchaResponse, optWidgetId) => {
      await loginAlias(
        values,
        {
          actions,
          onboardingActions: twofaActions,
        },
        captchaResponse,
      );
      resetReCaptcha(optWidgetId);
    };

    if (isGamma || byPassReCaptcha)
      loginAlias(values, {
        actions,
        onboardingActions: twofaActions,
      });
    else generateReCaptcha(onSubmitReCaptcha);
  };

  const handleEyeClick = () => {
    toggleEyeIcon(!showEyeIcon);
  };

  return (
    <FormikComponent
      initialValues={aliasLogin}
      validationSchema={validationSchema}
      onSubmit={submitAliasLogin(onboardingActions)}
    >
      {({
        values = {},
        handleFieldBlur,
        handleFieldChange,
        handleSubmit,
        errors,
        touched,
        ...formikProps
      }) => {
        const disabledField =
          !formikProps.isValidating && formikProps.isSubmitting;

        return (
          <Form autoComplete="off" onSubmit={handleSubmit} className="mt-3">
            <FormInput
              label="Account Name"
              name="accountAlias"
              value={values.accountAlias}
              errors={errors}
              touched={touched}
              onChange={handleFieldChange}
              onBlur={(e, data) =>
                captureBlur(
                  e,
                  data,
                  handleFieldBlur,
                  Analytics.trackEvent(
                    EVENTS[ALIAS_LOGIN].ACCOUNT_NAME,
                    { actionType: 'input' },
                    'OB',
                  ),
                )
              }
            />
            <FormInput
              label="Username"
              name="username"
              value={values.username}
              errors={errors}
              touched={touched}
              onChange={handleFieldChange}
              onBlur={(e, data) =>
                captureBlur(
                  e,
                  data,
                  handleFieldBlur,
                  Analytics.trackEvent(
                    EVENTS[ALIAS_LOGIN].USERNAME,
                    { actionType: 'input' },
                    'OB',
                  ),
                )
              }
            />
            <FormInput
              label="Password"
              name="password"
              type={showEyeIcon ? 'text' : 'password'}
              value={values.password}
              errors={errors}
              touched={touched}
              onChange={handleFieldChange}
              onBlur={(e, data) =>
                captureBlur(
                  e,
                  data,
                  handleFieldBlur,
                  Analytics.trackEvent(
                    EVENTS[ALIAS_LOGIN].PASSWORD,
                    { actionType: 'input' },
                    'OB',
                  ),
                )
              }
              icon={
                <Space alignItems="center" className="icon-wrapper">
                  <CustomIcon
                    componentName={showEyeIcon ? 'EYE_SLASH' : 'EYE'}
                    onClick={handleEyeClick}
                  />
                </Space>
              }
            />
            <Button
              id="recaptcha"
              fluid
              primary
              type="submit"
              disabled={disabledField}
              className="mr-top-16"
            >
              Log In
            </Button>
          </Form>
        );
      }}
    </FormikComponent>
  );
}

AliasLogin.propTypes = {
  aliasLogin: PropTypes.object.isRequired,
  navigateAsPerUserFlow: PropTypes.func.isRequired,
  authenticateAlias: PropTypes.func.isRequired,
  enableLoader: PropTypes.func.isRequired,
  onboardingActions: PropTypes.object.isRequired,
};

export default memo(AliasLogin);
